import React, {useState, ChangeEvent, FormEvent} from 'react';

import {notifications} from '@mantine/notifications';

import springboard from 'springboard';
import {DBObjects} from '@/store/store_types';
import {useModule} from '@/store/kysely_module';
import {StateSupervisor} from 'springboard/services/states/shared_state_service';
import {Button} from '@mantine/core';
import {IconFolder, IconPaperclip} from '@tabler/icons-react';
import {shouldIgnoreFile} from '@/utils/file_utils';
import {isReactNativeWeb} from '@/utils/user_agent';

declare module 'springboard/module_registry/module_registry' {
    interface AllModules {
        MSFiles: MSFilesModuleReturnValue;
    }
}

type FileProgressResult = {
    name: string;
    size: number;
    progressSupervisor: StateSupervisor<number>;
    finish: () => Promise<StoredFileMeta>;
}

export type StoredFileMetaWithDurationAndWaveformData = StoredFileMeta & {
    duration: number | null;
    waveform_data: string | null;
}

export type HashData = {
    relativePath: string;
    hash: string;
    size: number;
}

type IsDirectoryCallbackArgs = {
    directoryPath: string;
    computeHashes: (onProgress?: (current: number, total: number) => void) => Promise<HashData[]>;
    uploadFiles: (filePaths: string[], callbacks: DirectoryUploadCallbacks) => Promise<StoredFileMetaWithDurationAndWaveformData[]>;
}

export type DirectoryUploadCallbacks = {
    onUploadStart: OnUploadStart;
    onUploadEnd: (fileName: string) => void;
    onUploadProgress?: (fileName: string, progress: {uploaded: number; total: number}) => void;
}

type UploadCallbacks = {
    shouldUseDirectory: boolean;
    onUploadStart: OnUploadStart;
    onAbort?: () => void;
    onIsDirectory: (args: IsDirectoryCallbackArgs) => void;
    onUploadProgress?: (fileName: string, progress: {uploaded: number; total: number}) => void;
    onHasLargeFile?: () => void;
}

type OnUploadStart = (fileName: string, args?: {avoidToast?: boolean}) => void;

export type FileClient = {
    chooseFileAndUpload: (workspaceId: string, callbacks: UploadCallbacks) => Promise<StoredFileMetaWithDurationAndWaveformData | null>;
    getFileSrc: (fileInfo: DBObjects['File']) => Promise<string>;
}

export type MSFilesModuleReturnValue = {
    setFileClient: (client: FileClient) => void;
    client: () => FileClient;
};


springboard.registerModule('MSFiles', {}, async (moduleAPI): Promise<MSFilesModuleReturnValue> => {
    const remoteFileClientModule = moduleAPI.getModule('RemoteFileClient');
    let fileClient: FileClient | undefined = remoteFileClientModule?.client();

    return {
        setFileClient: client => {fileClient = client;},
        client: () => fileClient!,
    };
});

export type StoredFileMeta = {
    name: string;
    mimeType: string;
    fullPath: string;
    size: number;
    hash: string;
    lastModifiedAt?: number;
}

type Props = {
    workspaceId: string;
    onUpload: (args: {fileInfo: StoredFileMetaWithDurationAndWaveformData}) => void;
    onUploadStart: (fileName: string) => void;
    onDirectoryUploadStart: (args: {directoryPath: string; hashDatas: HashData[]}) => Promise<string[] | null>;
    onDirectoryUploadEnd: (args: {fileInfos: StoredFileMetaWithDurationAndWaveformData[]}) => void;
    disabled?: boolean;
};

const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

export const FileUpload = (props: Props) => {
    const [message, setMessage] = useState<string>('');

    const fileUploader = useModule('MSFiles').client();

    const [isUploading, setIsUploading] = useState(false);

    const isOfflineMode = useModule('User').isOfflineMode.useState();

    // const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    //     if (e.target.files) {
    //         setFile(e.target.files[0]);
    //         setMessage('');
    //     }
    // };

    const handleUpload = async (shouldUseDirectory: boolean) => {
        // if (!file) {
        //     setMessage('Please select a file to upload');
        //     return;
        // }

        const workspaceId = props.workspaceId;

        setIsUploading(true);
        // setMessage('Uploading');

        let statusNotificationId: string | undefined = undefined;
        let currentFileNotificationId: string | undefined = undefined;
        let analyzingNotificationId: string | undefined = undefined;

        fileUploader.chooseFileAndUpload(workspaceId, {
            shouldUseDirectory,
            onUploadStart: (fileName, args) => {
                props.onUploadStart(fileName);
                if (analyzingNotificationId) {
                    notifications.hide(analyzingNotificationId);
                    analyzingNotificationId = undefined;
                }
                if (!args?.avoidToast) {
                    currentFileNotificationId = notifications.show({
                        loading: true,
                        title: `Uploading ${fileName}`,
                        message: 'Starting upload...',
                        autoClose: false,
                        onAbort: () => {
                            // handle abort
                        },
                    });
                }
            },
            onHasLargeFile: () => {
                analyzingNotificationId = notifications.show({
                    loading: true,
                    title: 'Analyzing files...',
                    message: '',
                    autoClose: false,
                });
            },
            onUploadProgress: (fileName, progress) => {
                const percentage = Math.round((progress.uploaded / progress.total) * 100);
                const uploadedFormatted = formatFileSize(progress.uploaded);
                const totalFormatted = formatFileSize(progress.total);
                
                if (currentFileNotificationId) {
                    notifications.update({
                        id: currentFileNotificationId,
                        loading: true,
                        title: `Uploading ${fileName}`,
                        message: `${percentage}% - ${uploadedFormatted} / ${totalFormatted}`,
                        autoClose: false,
                    });
                }
            },
            onAbort: () => {
                // Object.values(notificationIds).forEach((notificationId) => {
                // if (notificationId) {
                //     notifications.hide(notificationId);
                // }

                // setIsUploading(false);
            },
            onIsDirectory: async (args) => {
                const analyzingDirectoryNotificationId = notifications.show({
                    loading: true,
                    title: `Analyzing local directory`,
                    message: (
                        <div>
                            {args.directoryPath}
                        </div>
                    ),
                    autoClose: false,
                });

                const originalHashDatas = await args.computeHashes((current, total) => {
                    notifications.update({
                        id: analyzingDirectoryNotificationId,
                        loading: true,
                        title: `Analyzing local directory`,
                        message: (
                            <div>
                                {args.directoryPath}
                                <br />
                                <small>Processed {current} of {total} files</small>
                            </div>
                        ),
                        autoClose: false,
                    });
                }).finally(() => {
                    notifications.hide(analyzingDirectoryNotificationId);
                    if (analyzingNotificationId) {
                        notifications.hide(analyzingNotificationId);
                    }
                });

                const fileNamesToUpload = await props.onDirectoryUploadStart({
                    directoryPath: args.directoryPath,
                    hashDatas: originalHashDatas,
                });

                if (!fileNamesToUpload?.length) {
                    notifications.show({
                        title: '\u2705 All files already up to date',
                        message: '',
                        color: 'green',
                    });
                    return;
                }

                let numFilesUploaded = 0;
                const hashDatas = originalHashDatas.filter(hashData => {
                    return fileNamesToUpload.includes(hashData.relativePath)
                        && !shouldIgnoreFile(hashData.relativePath)
                        && Boolean(hashData.size)
                });

                const totalNumFiles = hashDatas.length;

                // for (const hashData of hashDatas) {
                //     // call props.something() to handle which files need to be uploaded
                //     // this should check if the directory name exists in an existing project
                //     // if it does, we'll upload to that given project
                //     // and determine which files need to be uploaded in the parent component maybe
                //     // maybe want to map out how thinga currently work on paper
                //     // like all function call flows of all platforms
                //     // and potentially rearrange things as needed to support directories efficiently
                // }

                if (!hashDatas.length) {
                    notifications.show({
                        title: '\u2705 All files already up to date',
                        message: '',
                        color: 'green',
                    });
                    return;
                }

                statusNotificationId = notifications.show({
                    loading: true,
                    title: 'Folder upload progress',
                    message: `Uploading ${Math.min(numFilesUploaded + 1, totalNumFiles)} / ${totalNumFiles} files`,
                    autoClose: false,
                });

                const fileInfos = await args.uploadFiles(hashDatas.map(h => h.relativePath), {
                    onUploadStart: (fileName, onUploadStartArgs) => {
                        if (!onUploadStartArgs?.avoidToast) {
                            if (currentFileNotificationId) {
                                notifications.update({
                                    id: currentFileNotificationId,
                                    title: `Uploading ${fileName}`,
                                    message: 'Starting upload...',
                                });
                            } else {
                                currentFileNotificationId = notifications.show({
                                    loading: true,
                                    title: `Uploading ${fileName}`,
                                    message: 'Starting upload...',
                                    autoClose: false,
                                    onAbort: () => {
                                        // handle abort
                                    },
                                });
                            }
                        }
                    },
                    onUploadProgress: (fileName, progress) => {
                        const percentage = Math.round((progress.uploaded / progress.total) * 100);
                        const uploadedFormatted = formatFileSize(progress.uploaded);
                        const totalFormatted = formatFileSize(progress.total);
                        
                        if (currentFileNotificationId) {
                            notifications.update({
                                id: currentFileNotificationId,
                                loading: true,
                                title: `Uploading ${fileName}`,
                                message: `${percentage}% - ${uploadedFormatted} / ${totalFormatted}`,
                                autoClose: false,
                            });
                        }
                    },
                    onUploadEnd: (fileName) => {
                        numFilesUploaded++;

                        if (!totalNumFiles) {
                            return;
                        }

                        if (statusNotificationId) {
                            if (numFilesUploaded < totalNumFiles) {
                                notifications.update({
                                    id: statusNotificationId,
                                    message: `Uploading ${Math.min(numFilesUploaded + 1, totalNumFiles)} / ${totalNumFiles} files`,
                                });
                            } else {
                                notifications.hide(statusNotificationId);
                                if (currentFileNotificationId) {
                                    notifications.hide(currentFileNotificationId);
                                }
                            }
                        }
                    },
                });

                props.onDirectoryUploadEnd({fileInfos});
            },
        })
            .then((result) => {
                setIsUploading(false);
                if (currentFileNotificationId) {
                    notifications.hide(currentFileNotificationId);
                }
                if (statusNotificationId) {
                    notifications.hide(statusNotificationId);
                }

                if (!result) {
                    setMessage('');
                    return;
                }

                props.onUpload({
                    fileInfo: result,
                });
                setMessage(`\u2705 File uploaded: ${result.name}`);
            })
            .catch(e => {
                if (currentFileNotificationId) {
                    setTimeout(() => {
                        notifications.hide(currentFileNotificationId!);
                    }, 1000);
                }
                if (statusNotificationId) {
                    setTimeout(() => {
                        notifications.hide(statusNotificationId!);
                    }, 1000);
                }

                notifications.show({
                    title: 'Error uploading file',
                    message: '',
                    autoClose: true,
                });

                console.error('Error uploading file', e);

                setIsUploading(false);
                setMessage((e as Error).message);
            });
    };

    return (
        <div>
            <form>
                {/* <input type="file" onChange={handleFileChange} /> */}
                <Button
                    mr='xs'
                    id='choose-file-button'
                    type='submit'
                    size='xs'
                    variant='filled'
                    leftSection={<IconPaperclip size={15} />}
                    disabled={props.disabled || isUploading}
                    onClick={e => {
                        e.preventDefault();
                        handleUpload(false);
                    }}
                >
                    Upload File
                </Button>
                {(!isOfflineMode && !isReactNativeWeb()) && (
                    <>
                        or
                        <Button
                            ml='xs'
                            id='choose-folder-button'
                            type='submit'
                            size='xs'
                            variant='filled'
                            leftSection={<IconFolder size={15} />}
                            disabled={props.disabled || isUploading}
                            onClick={e => {
                                e.preventDefault();
                                handleUpload(true);
                            }}
                        >
                            Upload Folder
                        </Button>
                    </>
                )}
            </form>
            {message && <p>{message}</p>}
        </div>
    );
}

export default FileUpload;
